import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVaultStore, Vault } from '../../../store/vaultStore';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/Select';
import { Plus, KeyRound, Users, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';

const TABS: { key: 'all' | 'personal' | 'team'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'personal', label: 'Personal' },
  { key: 'team', label: 'Team' },
];

export function VaultList() {
  const navigate = useNavigate();
  const { vaults, isLoading, fetchVaults, createVault } = useVaultStore();
  const [tab, setTab] = useState<'all' | 'personal' | 'team'>('all');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ vaultType: 'personal', name: '', description: '' });

  useEffect(() => {
    fetchVaults(tab === 'all' ? undefined : tab);
  }, [tab, fetchVaults]);

  const submit = async () => {
    if (form.vaultType === 'team' && !form.name.trim()) {
      toast.error('A team vault needs a name');
      return;
    }
    setSaving(true);
    try {
      const v = await createVault({
        vaultType: form.vaultType,
        name: form.name.trim() || undefined,
        description: form.description.trim() || undefined,
      });
      toast.success('Vault created');
      setOpen(false);
      setForm({ vaultType: 'personal', name: '', description: '' });
      navigate(`/vault/vaults/${v.id}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to create vault');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Vaults</h1>
          <p className="text-muted-foreground">Your personal vault and the team vaults you can access.</p>
        </div>
        <Button variant="primary" onClick={() => setOpen(true)}>
          <Plus className="w-4 h-4 mr-2" /> New vault
        </Button>
      </div>

      <div className="flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <h3 className="text-foreground font-display text-section-title">
            {vaults.length} vault{vaults.length === 1 ? '' : 's'}
          </h3>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Loading…</div>
          ) : vaults.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground">
              No vaults yet. Create your personal vault to get started.
            </div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                <tr>
                  <th className="px-6 py-3">Vault</th>
                  <th className="px-6 py-3">Type</th>
                  <th className="px-6 py-3">Items</th>
                  <th className="px-6 py-3">Your access</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {vaults.map((v: Vault) => (
                  <tr
                    key={v.id}
                    className="hover:bg-secondary/20 transition-colors cursor-pointer"
                    onClick={() => navigate(`/vault/vaults/${v.id}`)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          {v.vaultType === 'team' ? <Users className="w-4 h-4" /> : <KeyRound className="w-4 h-4" />}
                        </div>
                        <div>
                          <div className="font-medium text-foreground">{v.name}</div>
                          {v.description && <div className="text-xs text-muted-foreground truncate max-w-xs">{v.description}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 capitalize">{v.vaultType}</td>
                    <td className="px-6 py-4">{v.itemCount ?? 0}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {v.permissions?.reveal && <Badge variant="success">Reveal</Badge>}
                        {v.permissions?.edit && <Badge variant="secondary">Edit</Badge>}
                        {v.permissions?.share && <Badge variant="secondary">Share</Badge>}
                        {!v.permissions?.edit && !v.permissions?.reveal && <Badge variant="muted">View</Badge>}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={v.status === 'active' ? 'success' : 'secondary'}>{v.status.toUpperCase()}</Badge>
                    </td>
                    <td className="px-6 py-4 text-right text-muted-foreground">
                      <ChevronRight className="w-4 h-4 inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New vault</DialogTitle>
            <DialogDescription>
              A personal vault is private to you. A team vault can have members with explicit permissions.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Type</label>
              <Select value={form.vaultType} onValueChange={(v) => setForm((f) => ({ ...f, vaultType: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Name {form.vaultType === 'personal' && <span className="text-muted-foreground">(optional)</span>}
              </label>
              <Input
                placeholder={form.vaultType === 'team' ? 'e.g. Platform Infra' : 'Personal Vault'}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Description</label>
              <Textarea
                rows={3}
                placeholder="What is this vault for?"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={submit} isLoading={saving}>Create vault</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
